import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import {
	InspectionTemplate,
	InspectionTemplateDraft,
	InspectionTemplateSection,
	TEMPLATE_INITIAL_VALUES,
} from "@/shared/models/Inspection";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useInspectionTemplateCatalog } from "../hooks/useInspectionTemplateCatalog";
import { useTableData } from "../hooks/useTableData";
import { useTemplateOptionsFunctions } from "../hooks/useTemplateOptionsFunctions";
import {
	asDraft,
	asDuplicateDraft,
	asFirstRevisionDraft,
	asRevisionDraft,
	sectionRevisions,
} from "../utils/templateDrafts";
import { TemplateModalMode, TemplateModalTarget } from "../utils/templateModal";
import { TemplateRevisionsPanel } from "./TemplateRevisionsPanel";
import { UpsertDeleteTemplateModal } from "./UpsertDeleteTemplateModal";

export const TableInspectionTemplates = () => {
	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [target, setTarget] = useState<TemplateModalTarget | null>(null);
	const [error, setError] = useState("");

	const { data, isLoading } = useInspectionTemplateCatalog((queryError) =>
		setError(queryError.message),
	);

	const templates = data?.templates ?? [];
	const columns = data?.columns ?? [];
	const sections = data?.sections ?? [];

	const open = (
		mode: TemplateModalMode,
		draft: InspectionTemplateDraft,
		sectionName: string,
		inspectionCount = 0,
	) =>
		setTarget({
			openedAt: Date.now(),
			mode,
			draft,
			sectionName,
			inspectionCount,
		});

	const newTemplate = () => {
		gridApi?.deselectAll();
		open("create", TEMPLATE_INITIAL_VALUES, "");
	};

	const newRevisionFor = (section: InspectionTemplateSection) => {
		const latest = sectionRevisions(templates, section.SectionId).latest;

		open(
			"revision",
			latest === null
				? asFirstRevisionDraft(section.SectionId, new Date())
				: asRevisionDraft(templates, columns, latest, new Date()),
			section.SectionName,
		);
	};

	const duplicateForSection = (template: InspectionTemplate) =>
		open("duplicate", asDuplicateDraft(columns, template), "");

	const editTemplate = (template: InspectionTemplate) =>
		open(
			"update",
			asDraft(template, columns),
			template.SectionName,
			template.InspectionCount,
		);

	const viewTemplate = (template: InspectionTemplate) =>
		open(
			"view",
			asDraft(template, columns),
			template.SectionName,
			template.InspectionCount,
		);

	const deleteTemplate = (template: InspectionTemplate) =>
		open("delete", asDraft(template, columns), template.SectionName);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useTemplateOptionsFunctions({ newRevisionFor });
	const { columnDefs } = useTableData({ newRevisionFor, showContextMenu });
	const { defaultColDef } = useGridDefinitions({});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "inspectionTemplateContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[sections],
	);

	useEffect(() => {
		registerConfig({
			id: "inspectionTemplateContext",
			getOptions: getMenuOptions,
		});
	}, [data]);

	return (
		<Grid2 container justifyContent="center" p={1} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Inspection Templates
				</Typography>
				<Typography variant="body2" color="textSecondary">
					The sheet each section is inspected with on this mill. Open a section
					to read the revisions it has spent; which one a sheet opens on is read
					off its effective date.
				</Typography>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 12 }}>
				<TextFieldSearchFilter
					value={filterValue}
					onChange={(value) => {
						setFilterValue(value);
						gridApi?.setGridOption("quickFilterText", value);
					}}
				/>
			</Grid2>
			<Grid2 container justifyContent="center" alignItems="center">
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper
						elevation={2}
						sx={{
							height: "70vh",
							width: "100%",
							"& .ag-details-row": { padding: 0 },
						}}
					>
						<AgGridReact<InspectionTemplateSection>
							loading={isLoading}
							rowData={sections}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							masterDetail
							detailCellRenderer={TemplateRevisionsPanel}
							detailCellRendererParams={{
								revisions: templates,
								onEdit: editTemplate,
								onView: viewTemplate,
								onDuplicate: duplicateForSection,
								onDelete: deleteTemplate,
							}}
							detailRowAutoHeight
							getContextMenuItems={getContextMenuItems}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) => `${params.data.SectionId}`}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<FooterButton
					label="TEMPLATE"
					showCreateModal={target?.mode === "create"}
					setShowCreateModal={(show) =>
						show ? newTemplate() : setTarget(null)
					}
					sizes={{ md: 3 }}
				/>
			</Grid2>
			<UpsertDeleteTemplateModal
				target={target}
				onHide={() => setTarget(null)}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
